import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ROLES, ROLE_LABELS, type RoleCode } from '@/auth/permissions';
import { useAuth } from '@/auth/useAuth';

/**
 * Dev-only, gated on `VITE_USE_MOCKS`. This is how acceptance criterion
 * **C-09** — ten roles, ten landing routes, a zone-scoped user proven limited
 * — gets demonstrated without an Entra tenant.
 *
 * It talks to the mock's session directly rather than through the API,
 * because it is a demo affordance and not a product feature. When
 * `VITE_USE_MOCKS=false` it renders nothing at all.
 */

const MOCKS_ENABLED = import.meta.env.VITE_USE_MOCKS !== 'false';

/** The zone scope the figures use for A. Nasser: 3 of 15 areas. */
const DEMO_SCOPE = ['6-A', '6-B', '6-C'];

export function RoleSwitcher() {
  const { me, refresh } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // From `/me`, so the switcher does not need the mock's own tables.
  const totalAreas = me?.zoneScope.totalZoneCount ?? 15;

  if (!MOCKS_ENABLED) return null;

  const apply = async (roles: RoleCode[] | null, zoneCodes: string[] | null) => {
    setBusy(true);
    const { setSession } = await import('@/mocks/db/store');
    setSession({ roleOverride: roles, zoneScopeOverride: zoneCodes });
    await refresh();
    await queryClient.invalidateQueries();
    setBusy(false);
  };

  return (
    <div className="fixed bottom-3 right-3 z-50 text-table">
      {open ? (
        <div className="w-[280px] pv-card p-3 shadow-lg">
          <div className="mb-2 flex items-center justify-between">
            <p className="font-semibold text-pv-ink">Demo role</p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close the role switcher"
              className="px-1 text-pv-muted"
            >
              ×
            </button>
          </div>
          <p className="mb-2 text-dense text-pv-muted">
            Mock sessions only. Nothing here exists when <code>VITE_USE_MOCKS=false</code>.
          </p>

          <label htmlFor="pv-demo-role" className="pv-eyebrow mb-1 block text-pv-muted">
            Role
          </label>
          <select
            id="pv-demo-role"
            className="mb-3 w-full pv-field px-2 py-1.5"
            value={me?.roles[0] ?? ''}
            disabled={busy}
            onChange={(event) => void apply([event.target.value as RoleCode], null)}
          >
            {ROLES.map((role) => (
              <option key={role} value={role}>
                {ROLE_LABELS[role]}
              </option>
            ))}
          </select>

          <p id="pv-demo-scope" className="pv-eyebrow mb-1 text-pv-muted">
            Zone scope
          </p>
          <div className="flex gap-2" role="group" aria-labelledby="pv-demo-scope">
            <button
              type="button"
              disabled={busy}
              onClick={() => void apply(me?.roles as RoleCode[], [])}
              className={`flex-1 rounded border px-2 py-1.5 ${
                me?.zoneScope.limited
                  ? 'border-pv-line text-pv-muted'
                  : 'border-pv-primary bg-pv-primary text-white'
              }`}
            >
              All {totalAreas} areas
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void apply(me?.roles as RoleCode[], DEMO_SCOPE)}
              className={`flex-1 rounded border px-2 py-1.5 ${
                me?.zoneScope.limited
                  ? 'border-pv-primary bg-pv-primary text-white'
                  : 'border-pv-line text-pv-muted'
              }`}
            >
              {DEMO_SCOPE.length} of {totalAreas}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="rounded-xs border border-pv-line bg-pv-surface px-3 py-1.5 font-semibold text-pv-primary shadow"
        >
          Demo role
        </button>
      )}
    </div>
  );
}
